import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';

// API_URL should already include /api suffix from REACT_APP_API_URL
const API_URL = process.env.REACT_APP_API_URL || 'https://api.exploreandwin.com/api';
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initializeAuth = () => {
      console.log('[AUTH] Initializing auth state...');
      try {
        const storedUser = localStorage.getItem('cms_user');
        const accessToken = localStorage.getItem('cms_accessToken');
        console.log('[AUTH] Stored user:', storedUser ? 'exists' : 'null');
        console.log('[AUTH] Access token:', accessToken ? 'exists' : 'null');

        if (storedUser && accessToken) {
          const userData = JSON.parse(storedUser);
          console.log('[AUTH] User data:', userData.username, userData.role);

          // Basic token expiration check
          try {
            const tokenData = JSON.parse(atob(accessToken.split('.')[1]));
            const currentTime = Date.now() / 1000;
            const expiresIn = tokenData.exp - currentTime;
            console.log('[AUTH] Token expires in:', Math.floor(expiresIn / 60), 'minutes');

            if (tokenData.exp > currentTime) {
              console.log('[AUTH] Token valid, setting authenticated state');
              setUser(userData);
              setIsAuthenticated(true);
            } else {
              console.log('[AUTH] Token expired, attempting refresh');
              // Token expired, try to refresh
              refreshToken();
            }
          } catch (tokenError) {
            console.error('[AUTH] Token decode error:', tokenError);
            clearAuth();
          }
        } else {
          console.log('[AUTH] No stored credentials found');
        }
      } catch (error) {
        console.error('[AUTH] Auth initialization error:', error);
        clearAuth();
      } finally {
        setIsLoading(false);
        console.log('[AUTH] Auth initialization complete');
      }
    };

    initializeAuth();
  }, []);

  const refreshToken = async () => {
    try {
      const refreshToken = localStorage.getItem('cms_refreshToken');
      if (!refreshToken) {
        clearAuth();
        return false;
      }

      console.log('[DEBUG] Attempting token refresh to:', `${API_URL}/cms/auth/refresh`);
      const response = await axios.post(`${API_URL}/cms/auth/refresh`, {
        refreshToken
      });

      const { tokens } = response.data;
      localStorage.setItem('cms_accessToken', tokens.accessToken);
      localStorage.setItem('cms_refreshToken', tokens.refreshToken);

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      clearAuth();
      return false;
    }
  };

  const login = async (username, password) => {
    console.log('[DEBUG] Login attempt started');
    console.log('[DEBUG] Username:', username);
    console.log('[DEBUG] API URL:', `${API_URL}/cms/auth/login`);

    try {
      console.log('[DEBUG] Sending login request...');
      const response = await axios.post(`${API_URL}/cms/auth/login`, {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: true,
        timeout: 10000
      });

      console.log('[DEBUG] Login response received:', response.data);
      const { user: userData, tokens } = response.data;
      console.log('[DEBUG] User data:', userData);
      console.log('[DEBUG] Tokens received:', tokens ? 'Yes' : 'No');

      // Store auth data
      console.log('[AUTH] Saving tokens to localStorage...');
      localStorage.setItem('cms_accessToken', tokens.accessToken);
      localStorage.setItem('cms_refreshToken', tokens.refreshToken);
      localStorage.setItem('cms_user', JSON.stringify(userData));

      // Verify saved
      const savedToken = localStorage.getItem('cms_accessToken');
      const savedUser = localStorage.getItem('cms_user');
      console.log('[AUTH] Verification - Token saved:', savedToken ? 'Yes' : 'No');
      console.log('[AUTH] Verification - User saved:', savedUser ? 'Yes' : 'No');

      setUser(userData);
      setIsAuthenticated(true);

      console.log('[DEBUG] Login successful, returning success');
      return { success: true, user: userData };
    } catch (error) {
      console.error('[DEBUG] Login error details:');
      console.error('[DEBUG] Error message:', error.message);
      console.error('[DEBUG] Response status:', error.response?.status);
      console.error('[DEBUG] Response data:', error.response?.data);
      console.error('[DEBUG] Full error:', error);
      return {
        success: false,
        error: error.response?.data?.message || error.response?.data?.error || error.message || 'Login failed',
        details: {
          status: error.response?.status,
          statusText: error.response?.statusText,
          data: error.response?.data,
          message: error.message
        }
      };
    }
  };

  const logout = async () => {
    try {
      const refreshToken = localStorage.getItem('cms_refreshToken');
      if (refreshToken) {
        console.log('[DEBUG] Logout attempt to:', `${API_URL}/cms/auth/logout`);
        await axios.post(`${API_URL}/cms/auth/logout`, {
          refreshToken
        });
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearAuth();
    }
  };

  const clearAuth = () => {
    console.log('[AUTH] Clearing auth state - redirecting to login');
    console.trace('[AUTH] clearAuth called from:');
    localStorage.removeItem('cms_accessToken');
    localStorage.removeItem('cms_refreshToken');
    localStorage.removeItem('cms_user');
    setUser(null);
    setIsAuthenticated(false);
  };

  const getAccessToken = () => {
    return localStorage.getItem('cms_accessToken');
  };

  const updateUser = (updatedUserData) => {
    const newUserData = { ...user, ...updatedUserData };
    setUser(newUserData);
    localStorage.setItem('cms_user', JSON.stringify(newUserData));
  };

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    logout,
    refreshToken,
    getAccessToken,
    clearAuth,
    updateUser
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;